/**
 * `<img>` wrapped in a `<picture>` with a WebP `srcset` when a batch-processed
 * variant exists for this file (see `lib/imageVariants.ts`); otherwise renders
 * a plain `<img>`. The original JPG is always the fallback `<img>` — browsers
 * without WebP support (or a photo that was never processed) never break.
 */
import { webpVariantUrl, type ImageVariantSize } from '../lib/imageVariants';

export interface PropertyPictureProps {
  src: string;
  alt: string;
  size: ImageVariantSize;
  className?: string;
  loading?: 'lazy' | 'eager';
}

export function PropertyPicture({
  src,
  alt,
  size,
  className,
  loading = 'lazy',
}: PropertyPictureProps): React.JSX.Element {
  const webp = webpVariantUrl(src, size);

  if (webp === null) {
    return <img className={className} src={src} alt={alt} loading={loading} />;
  }

  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img className={className} src={src} alt={alt} loading={loading} />
    </picture>
  );
}

export default PropertyPicture;
